/**
 * 보안 브라우저 환경에서의 URL 파라미터 처리 유틸리티
 */

export interface BrowserInfo {
  isSecureBrowser: boolean;
  isPrivateMode: boolean;
  browserType: string;
  supportsUrlSearchParams: boolean;
  supportsLocationSearch: boolean;
}

/**
 * 브라우저 환경 정보를 감지
 */
export const detectBrowserEnvironment = (): BrowserInfo => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  // 보안 브라우저 패턴들
  const secureBrowserPatterns = [
    'tor browser',
    'brave',
    'duckduckgo',
    'firefox focus',
    'samsung internet',
    'chrome incognito',
    'edge inprivate',
    'safari private'
  ];
  
  // Private/Incognito 모드 감지
  const isPrivateMode = (() => {
    try {
      // localStorage 접근 시도
      const testKey = '__private_mode_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return false;
    } catch (e) {
      return true;
    }
  })();
  
  // URLSearchParams 지원 여부 확인
  const supportsUrlSearchParams = (() => {
    try {
      new URLSearchParams('test=1');
      return true;
    } catch (e) {
      return false;
    }
  })();
  
  // location.search 지원 여부 확인
  const supportsLocationSearch = (() => {
    try {
      const search = window.location.search;
      return typeof search === 'string';
    } catch (e) {
      return false;
    }
  })();
  
  // 브라우저 타입 감지
  let browserType = 'unknown';
  if (userAgent.includes('chrome')) browserType = 'chrome';
  else if (userAgent.includes('firefox')) browserType = 'firefox';
  else if (userAgent.includes('safari')) browserType = 'safari';
  else if (userAgent.includes('edge')) browserType = 'edge';
  else if (userAgent.includes('brave')) browserType = 'brave';
  else if (userAgent.includes('tor')) browserType = 'tor';
  
  // 보안 브라우저 감지
  const isSecureBrowser = secureBrowserPatterns.some(pattern => 
    userAgent.includes(pattern)
  ) || isPrivateMode;
  
  return {
    isSecureBrowser,
    isPrivateMode,
    browserType,
    supportsUrlSearchParams,
    supportsLocationSearch
  };
};

/**
 * 보안 브라우저 환경에서 URL 파라미터를 안전하게 추출
 */
export const extractUrlParamsSafely = (): {
  timestamp: string | null;
  hash: string | null;
  hasParams: boolean;
  extractionMethod: string;
} => {
  const browserInfo = detectBrowserEnvironment();
  let timestamp: string | null = null;
  let hash: string | null = null;
  let hasParams = false;
  let extractionMethod = 'none';
  
  try {
    // 방법 1: URLSearchParams 사용 (가장 안전)
    if (browserInfo.supportsUrlSearchParams) {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        timestamp = searchParams.get('t');
        hash = searchParams.get('h');
        hasParams = !!(timestamp && hash);
        if (hasParams) {
          extractionMethod = 'urlsearchparams';
          return { timestamp, hash, hasParams, extractionMethod };
        }
      } catch (e) {
        console.warn('URLSearchParams 방식 실패:', e);
      }
    }
    
    // 방법 2: URL 객체 사용
    if (browserInfo.supportsLocationSearch) {
      try {
        const url = new URL(window.location.href);
        timestamp = url.searchParams.get('t');
        hash = url.searchParams.get('h');
        hasParams = !!(timestamp && hash);
        if (hasParams) {
          extractionMethod = 'url_object';
          return { timestamp, hash, hasParams, extractionMethod };
        }
      } catch (e) {
        console.warn('URL 객체 방식 실패:', e);
      }
    }
    
    // 방법 3: 정규식으로 직접 파싱 (최후의 수단)
    try {
      const urlString = window.location.href;
      const timestampMatch = urlString.match(/[?&]t=(\d{10})/);
      const hashMatch = urlString.match(/[?&]h=([a-f0-9]{16})/);
      
      if (timestampMatch && hashMatch) {
        timestamp = timestampMatch[1];
        hash = hashMatch[1];
        hasParams = true;
        extractionMethod = 'regex';
        return { timestamp, hash, hasParams, extractionMethod };
      }
    } catch (e) {
      console.warn('정규식 파싱 방식 실패:', e);
    }
    
    // 방법 4: document.referrer 사용 (보안 브라우저에서 제한될 수 있음)
    try {
      if (document.referrer) {
        const referrerUrl = new URL(document.referrer);
        timestamp = referrerUrl.searchParams.get('t');
        hash = referrerUrl.searchParams.get('h');
        hasParams = !!(timestamp && hash);
        if (hasParams) {
          extractionMethod = 'referrer';
          return { timestamp, hash, hasParams, extractionMethod };
        }
      }
    } catch (e) {
      console.warn('referrer 방식 실패:', e);
    }
    
  } catch (error) {
    console.error('URL 파라미터 추출 중 오류:', error);
  }
  
  return { timestamp, hash, hasParams, extractionMethod };
};

/**
 * 보안 브라우저 환경에서의 대체 인증 방법
 */
export const getAlternativeAuthMethod = (): string | null => {
  const browserInfo = detectBrowserEnvironment();
  
  if (browserInfo.isSecureBrowser) {
    // 보안 브라우저에서는 세션 기반 인증 우선
    return 'session_based';
  }
  
  if (browserInfo.isPrivateMode) {
    // Private 모드에서는 쿠키 기반 인증 시도
    return 'cookie_based';
  }
  
  return null;
};

/**
 * 보안 브라우저 환경에서의 에러 메시지 생성
 */
export const getSecureBrowserErrorMessage = (browserInfo: BrowserInfo): string => {
  if (browserInfo.isPrivateMode) {
    return 'Private/Incognito 모드에서는 URL 파라미터가 제한될 수 있습니다. 일반 브라우저 모드를 사용해주세요.';
  }
  
  if (browserInfo.isSecureBrowser) {
    return '보안 브라우저에서는 URL 파라미터 접근이 제한될 수 있습니다. 다른 브라우저를 시도해주세요.';
  }
  
  return '브라우저 환경에서 URL 파라미터를 읽을 수 없습니다.';
};

